import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import i18n from 'i18next';

import HealthIconSvg from 'common/images/HealthIconSvg';
import { Task, Wrench } from 'common/images/FaIcons';
import NoteSvgNew from 'common/images/NoteSvgNew';
import Text_T from '../../../common/typography/Text/Text';
import AlarmFlagSvg from '../../../common/images/AlarmFlagSvg';
import OverallAlarmWarningNew from '../../../common/images/OverallAlarmWarningNew';
import OverallAlarmCriticalNew from '../../../common/images/OverallAlarmCriticalNew';
import MoteUnreachableNew from '../../../common/images/MoteUnreachableNew';
import MachineSvg from '../../../common/images/MachineSvg';
import NewBatteryIcon from '../../../common/images/NewBatteryIcon';
import {
  getMoteUnreachableTitleText,
  getTransmitterUnreachableTitleText
} from '../helpers/activityEventDisplay';
import { humanize } from '../../../common/helpers/humanize';

import colors from '../../../common/styles/colors';
import { alarmLevel, overallMeasureLabel } from '../helpers/overallAlarmActivity';

export const Text = styled(Text_T)`
  font-size: 13px;
  ${props => props.hover && `
    &:hover {
      color: ${props.theme.primaryColor};
      cursor: pointer;
    }
  `}
`;

const DescriptionContainer = styled.div`
${props => props.marginTop && 'margin-top: 0.5em;'}
  > ${Text} {
    margin-bottom: 0.5em;
  }
`;

const getSpeedRangeText = (threshold_value) => {
  if (!threshold_value) return '';
  return i18n.t('activity.groupedActivity.speedRange', {
    defaultValue: 'for speed range - {{thresholdValue}}',
    thresholdValue: threshold_value
  });
};

const getClosedEventText = closedAt => (
  closedAt ? (
    <Text>
      {i18n.t('activity.groupedActivity.closedOn', {
        defaultValue: 'This event was closed on {{closedAt}}.',
        closedAt
      })}
    </Text>
  ) : null
);

const getTriggeredSpeedRangesText = (triggeredThresholdTypes, thresholdType) => {
  if (!triggeredThresholdTypes || triggeredThresholdTypes.length <= 1) return null;
  const ranges = triggeredThresholdTypes.filter(t => t !== thresholdType).join(', ');
  return (
    <Text>
      {i18n.t('activity.groupedActivity.triggeredOnSpeedRanges', {
        defaultValue: 'Also triggered on speed ranges - {{ranges}}',
        ranges
      })}
    </Text>
  );
};

const getFeedbackRaisedSpeedRangesText = speedInfo => (
  !_.isEmpty(speedInfo) ? (
    <Text>
      {i18n.t('activity.groupedActivity.feedbackRaisedSpeedRanges', {
        defaultValue: 'Feedback raised for speed ranges - {{ranges}}.',
        ranges: speedInfo.filter(info => info.speed_bin).map(info => info.speed_bin).join(', ')
      })}
    </Text>
  ) : null
);


const groupedActivityFormat = {
  vibration_warning: {
    title: activity => i18n.t('activity.groupedActivity.titles.vibrationWarning', {
      defaultValue: 'Vibration warning alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationsExceededWarning', {
            defaultValue: 'Vibrations have exceeded warning alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
            location: humanize(activity.location),
            componentName: activity.component_name,
            direction: activity.direction
          })}</Text>
          <Text>{i18n.t('activity.groupedActivity.messages.latestRmsExceededThreshold', {
            defaultValue: 'The latest RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
            rms: activity.rms,
            units: activity.units,
            thresholdValue: activity.threshold_value,
            percentIncrease: activity.percent_increase,
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmWarningNew />
  },
  vibration_critical: {
    title: activity => i18n.t('activity.groupedActivity.titles.vibrationCritical', {
      defaultValue: 'Vibration critical alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationsExceededCritical', {
            defaultValue: 'Vibrations have exceeded critical alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
            location: humanize(activity.location),
            componentName: activity.component_name,
            direction: activity.direction
          })}</Text>
          <Text>{i18n.t('activity.groupedActivity.messages.latestRmsExceededThreshold', {
            defaultValue: 'The latest RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
            rms: activity.rms,
            units: activity.units,
            thresholdValue: activity.threshold_value,
            percentIncrease: activity.percent_increase,
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  vibration_demod_warning: {
    title: activity => i18n.t('activity.groupedActivity.titles.demodVibrationWarning', {
      defaultValue: 'Demod vibration warning alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
            defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            alarmLevel: activity.alarm_level,
            location: humanize(activity.location),
            componentName: activity.component_name,
            direction: activity.direction
          })}</Text>
          <Text>{i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
            defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            rms: activity.rms,
            units: activity.units,
            thresholdValue: activity.threshold_value,
            percentIncrease: activity.percent_increase,
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmWarningNew />
  },
  vibration_demod_critical: {
    title: activity => i18n.t('activity.groupedActivity.titles.demodVibrationCritical', {
      defaultValue: 'Demod vibration critical alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
            defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            alarmLevel: activity.alarm_level,
            location: humanize(activity.location),
            componentName: activity.component_name,
            direction: activity.direction
          })}</Text>
          <Text>{i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
            defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            rms: activity.rms,
            units: activity.units,
            thresholdValue: activity.threshold_value,
            percentIncrease: activity.percent_increase,
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  vibration_acceleration_warning: {
    title: activity => i18n.t('activity.groupedActivity.titles.vibrationAccelerationWarning', {
      defaultValue: 'Vibration acceleration warning alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
            defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            alarmLevel: activity.alarm_level,
            location: humanize(activity.location),
            componentName: activity.component_name,
            direction: activity.direction
          })}</Text>
          <Text>{i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
            defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            rms: activity.rms,
            units: activity.units,
            thresholdValue: activity.threshold_value,
            percentIncrease: activity.percent_increase,
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmWarningNew />
  },
  vibration_acceleration_critical: {
    title: activity => i18n.t('activity.groupedActivity.titles.vibrationAccelerationCritical', {
      defaultValue: 'Vibration acceleration critical alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
            defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            alarmLevel: activity.alarm_level,
            location: humanize(activity.location),
            componentName: activity.component_name,
            direction: activity.direction
          })}</Text>
          <Text>{i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
            defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
            alarmLevelPrefix: activity.alarm_level_prefix,
            rms: activity.rms,
            units: activity.units,
            thresholdValue: activity.threshold_value,
            percentIncrease: activity.percent_increase,
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  vibration_envelope_alarm: {
    title: activity => i18n.t('activity.groupedActivity.titles.vibrationEnvelopeAlarm', {
      defaultValue: 'Vibration envelope alarm on the {{location}} at {{componentName}} in {{direction}} direction',
      location: humanize(activity.location),
      componentName: activity.component_name,
      direction: activity.direction
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.vibrationSpectrumExceededEnvelopeLimit', {
            defaultValue: 'Vibration spectrum exceeded envelope limit {{speedRange}}.',
            speedRange: getSpeedRangeText(activity.threshold_type)
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  current_envelope_alarm: {
    title: activity => i18n.t('activity.groupedActivity.titles.currentEnvelopeAlarm', {
      defaultValue: 'Current envelope alarm on the {{location}} at {{componentName}}',
      location: humanize(activity.location),
      componentName: activity.component_name
    }),
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.messages.currentSpectrumExceededEnvelope', {
            defaultValue: 'Current spectrum exceeded envelope limit.'
          })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  machine_condition_warning: {
    title: activity => i18n.t('activity.groupedActivity.titles.machineConditionAlarm', {
      defaultValue: '{{eventType}} alarm',
      eventType: humanize(activity.event_type)
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.messages.machineConditionWarningAt', {
          defaultValue: 'This machine condition has changed to warning at {{createdAt}}.',
          createdAt: activity.created_at
        })}</Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <MachineSvg fill={colors.warning} />
  },
  machine_condition_critical: {
    title: activity => i18n.t('activity.groupedActivity.titles.machineConditionAlarm', {
      defaultValue: '{{eventType}} alarm',
      eventType: humanize(activity.event_type)
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.messages.machineConditionCriticalAt', {
          defaultValue: 'This machine condition has changed to critical at {{createdAt}}.',
          createdAt: activity.created_at
        })}</Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <MachineSvg fill={colors.critical} />
  },
  health_score_warning: {
    title: () => i18n.t('activity.groupedActivity.healthScore.warningTitle', {
      defaultValue: 'Health score warning alarm'
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.healthScore.changedTo', {
          defaultValue: 'The health score {{changeDirection}} to {{value}} which is {{comparison}}.',
          changeDirection: activity.change_direction,
          value: activity.parameter_value,
          comparison: activity.comparision_string
        })}</Text>
        {activity.closed_at && (
          <Text>{i18n.t('activity.activityFormat.closedOn', {
            defaultValue: 'This event was closed on {{closedAt}}.',
            closedAt: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <AlarmFlagSvg fill={colors.alarmWarning} />
  },
  health_score_critical: {
    title: () => i18n.t('activity.groupedActivity.healthScore.criticalTitle', {
      defaultValue: 'Health score critical alarm'
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.healthScore.changedTo', {
          defaultValue: 'The health score {{changeDirection}} to {{value}} which is {{comparison}}.',
          changeDirection: activity.change_direction,
          value: activity.parameter_value,
          comparison: activity.comparision_string
        })}</Text>
        {activity.closed_at && (
          <Text>{i18n.t('activity.activityFormat.closedOn', {
            defaultValue: 'This event was closed on {{closedAt}}.',
            closedAt: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <AlarmFlagSvg fill={colors.alarmCritical} />
  },
  battery_level_warning: {
    title: () => i18n.t('activity.groupedActivity.battery.warningTitle', {
      defaultValue: 'Battery level warning alarm'
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.battery.moteLessThanRemaining', {
          defaultValue: 'The mote on {{location}} has less than {{batteryLevel}} battery remaining.',
          location: humanize(activity.location),
          batteryLevel: activity.battery_level
        })}</Text>
        {activity.closed_at && (
          <Text>{i18n.t('activity.activityFormat.closedOn', {
            defaultValue: 'This event was closed on {{closedAt}}.',
            closedAt: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill={colors.battery_level_warning} />
  },
  battery_level_critical: {
    title: activity => i18n.t('activity.groupedActivity.titles.batteryLevelCritical', {
      defaultValue: '{{serialNumber}} has a battery level critical alarm',
      serialNumber: activity.sensor_serial_number
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.battery.moteLessThanRemaining', {
          defaultValue: 'The mote on {{location}} has less than {{batteryLevel}} battery remaining.',
          location: humanize(activity.location),
          batteryLevel: activity.battery_level
        })}</Text>
        {activity.closed_at && (
          <Text>{i18n.t('activity.activityFormat.closedOn', {
            defaultValue: 'This event was closed on {{closedAt}}.',
            closedAt: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill={colors.battery_level_critical} />
  },
  tx_battery_level_warning: {
    title: activity => i18n.t('activity.groupedActivity.titles.txBatteryLevelWarning', {
      defaultValue: '{{serialNumber}} has a tx battery level warning alarm',
      serialNumber: activity.sensor_serial_number
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.battery.transmitterLessThanRemaining', {
          defaultValue: 'This transmitter has less than {{batteryLevel}} battery remaining.',
          batteryLevel: activity.battery_level
        })}</Text>
        {activity.closed_at && (
          <Text>{i18n.t('activity.activityFormat.closedOn', {
            defaultValue: 'This event was closed on {{closedAt}}.',
            closedAt: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill={colors.battery_level_warning} />
  },
  tx_battery_level_critical: {
    title: activity => i18n.t('activity.groupedActivity.titles.txBatteryLevelCritical', {
      defaultValue: '{{serialNumber}} has a battery level critical alarm',
      serialNumber: activity.sensor_serial_number
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.battery.transmitterLessThanRemaining', {
          defaultValue: 'This transmitter has less than {{batteryLevel}} battery remaining.',
          batteryLevel: activity.battery_level
        })}</Text>
        {activity.closed_at && (
          <Text>{i18n.t('activity.activityFormat.closedOn', {
            defaultValue: 'This event was closed on {{closedAt}}.',
            closedAt: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill={colors.battery_level_critical} />
  },
  mote_unreachable: {
    title: activity => getMoteUnreachableTitleText(i18n.t.bind(i18n), activity),
    description: activity => (
      <DescriptionContainer>
        {!activity.closed_at && (
          <>
            <Text>{i18n.t('activity.groupedActivity.messages.moteUnreachableSince', {
              defaultValue: 'This mote on {{location}} is unreachable since {{duration}}.',
              location: humanize(activity.location),
              duration: activity.duration
            })}</Text>
            <Text>{i18n.t('activity.groupedActivity.messages.sentLastReadingAt', {
              defaultValue: 'It sent last reading at {{time}}.',
              time: activity.last_reporting_time
            })}</Text>
          </>
        )}

        {activity.closed_at && (
          <Text>{i18n.t('activity.groupedActivity.messages.moteUnreachableBetween', {
            defaultValue: 'This mote on {{location}} was unreachable for {{duration}} between {{startTime}} and {{endTime}}.',
            location: humanize(activity.location),
            duration: activity.duration,
            startTime: activity.last_reporting_time,
            endTime: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <MoteUnreachableNew fill={colors.critical} />
  },
  tx_unreachable: {
    title: activity => getTransmitterUnreachableTitleText(i18n.t.bind(i18n), activity),
    description: activity => (
      <DescriptionContainer>
        {!activity.closed_at && (
          <>
            <Text>{i18n.t('activity.groupedActivity.messages.transmitterUnreachableSince', {
              defaultValue: 'This transmitter is unreachable since {{duration}}.',
              duration: activity.duration
            })}</Text>
            <Text>{i18n.t('activity.groupedActivity.messages.lastReportedAt', {
              defaultValue: 'It last reported at {{time}}.',
              time: activity.last_reporting_time
            })}</Text>
          </>
        )}
        {activity.closed_at && (
          <Text>{i18n.t('activity.groupedActivity.messages.transmitterUnreachableBetween', {
            defaultValue: 'This transmitter was unreachable for {{duration}} between {{startTime}} and {{endTime}}.',
            duration: activity.duration,
            startTime: activity.last_reporting_time,
            endTime: activity.closed_at
          })}</Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <AlarmFlagSvg fill={colors.alarmCritical} />
  },
  // Any other overall alarm. See the same entry in ActivityFormat.js.
  overall_alarm: {
    title: activity => i18n.t('activity.groupedActivity.titles.measureLevelAlarm', {
      defaultValue: '{{measure}} {{alarmLevel}} alarm{{locationPart}}{{directionPart}}',
      measure: overallMeasureLabel(activity),
      alarmLevel: i18n.t(`activity.activityFormat.alarmLevels.${alarmLevel(activity)}`, { defaultValue: alarmLevel(activity) }),
      locationPart: activity.location ? i18n.t('activity.groupedActivity.custom.titleParts.onThe', { defaultValue: ' on the {{location}}', location: humanize(activity.location) }) : '',
      directionPart: activity.direction ? i18n.t('activity.groupedActivity.custom.titleParts.inDirection', { defaultValue: ' in {{direction}} direction', direction: activity.direction }) : ''
    }),
    description: (activity) => {
      if (activity.rms === undefined) return groupedActivityFormat.custom.description(activity);

      return (
        <>
          <DescriptionContainer>
            <Text>{i18n.t('activity.groupedActivity.messages.latestMeasureExceededThreshold', {
              defaultValue: 'The latest {{measure}} of {{value}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              measure: activity.measure,
              value: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
            </Text>
            {getClosedEventText(activity.closed_at)}
          </DescriptionContainer>
          <DescriptionContainer marginTop>
            {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
          </DescriptionContainer>
        </>
      );
    },
    icon: activity => (alarmLevel(activity) === 'critical' ? <OverallAlarmCriticalNew /> : <OverallAlarmWarningNew />)
  },
  custom: {
    title: activity => i18n.t('activity.groupedActivity.custom.title', {
      defaultValue: '{{eventType}} alarm{{locationPart}}{{directionPart}}',
      eventType: humanize(activity.event_type),
      locationPart: activity.location ? i18n.t('activity.groupedActivity.custom.titleParts.onThe', { defaultValue: ' on the {{location}}', location: humanize(activity.location) }) : '',
      directionPart: activity.direction ? i18n.t('activity.groupedActivity.custom.titleParts.inDirection', { defaultValue: ' in {{direction}} direction', direction: activity.direction }) : '',
    }),
    description: activity => (
      <>
      {activity.parameter_type !== 'health_score' ? (
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.custom.parameterChanged', {
            defaultValue: 'The {{parameterType}} {{locationPart}}{{changeDirection}} to {{value}} which is {{comparison}}.',
            parameterType: activity.parameter_type,
            locationPart: activity.location ? i18n.t('activity.groupedActivity.custom.parameterChangedLocationPart', { defaultValue: 'on {{location}} ', location: humanize(activity.location) }) : '',
            changeDirection: activity.change_direction,
            value: activity.parameter_value,
            comparison: activity.comparision_string,
          })}</Text>
          {activity.closed_at && (
            <Text>{i18n.t('activity.activityFormat.closedOn', {
              defaultValue: 'This event was closed on {{closedAt}}.',
              closedAt: activity.closed_at
            })}</Text>
          )}
        </DescriptionContainer>
      ) : (
        <DescriptionContainer>
          <Text>{i18n.t('activity.groupedActivity.healthScore.changedTo', {
            defaultValue: 'The health score {{changeDirection}} to {{value}} which is {{comparison}}.',
            changeDirection: activity.change_direction,
            value: activity.parameter_value,
            comparison: activity.comparision_string
          })}</Text>
          {activity.closed_at && (
            <Text>{i18n.t('activity.activityFormat.closedOn', {
              defaultValue: 'This event was closed on {{closedAt}}.',
              closedAt: activity.closed_at
            })}</Text>
          )}
        </DescriptionContainer>
      )}
      </>
    ),
    icon: activity => <AlarmFlagSvg fill={activity.event_type && activity.event_type.includes('critical') ? colors.alarmCritical : colors.alarmWarning} />
  },
  diagnosis: {
    title: activity => i18n.t('activity.groupedActivity.diagnosis.title', {
      defaultValue: '{{ruleName}} triggered{{locationPart}}',
      ruleName: activity.rule_name,
      locationPart: activity.location ? i18n.t('activity.groupedActivity.diagnosis.titleParts.at', { defaultValue: ' at {{location}}', location: humanize(activity.location) }) : '',
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{activity.recommendation}</Text>
      </DescriptionContainer>
    ),
    icon: () => <HealthIconSvg />
  },
  new_operation_modes: {
    title: () => i18n.t('activity.groupedActivity.newOperatingMode.title', {
      defaultValue: 'New operating mode detected'
    }),
    description: () => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.newOperatingMode.description', { defaultValue: 'Re-configure VFD speed ranges.' })}</Text>
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  baseline_missing_for_operation_modes: {
    title: activity => i18n.t('activity.groupedActivity.baselineMissing.title', {
      defaultValue: 'Has baselines missing for few operating modes at {{componentName}}',
      componentName: activity.component_name
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.baselineMissing.description', { defaultValue: 'Re-baseline to include these operation modes.' })}</Text>
        {getFeedbackRaisedSpeedRangesText(activity.speed_info)}
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  baseline_higher_than_measurement_value: {
    title: activity => i18n.t('activity.groupedActivity.baselineHigher.title', {
      defaultValue: 'Vibrations changed from earlier indicated normal behavior at {{componentName}}',
      componentName: activity.component_name
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.baselineHigher.description', { defaultValue: 'Re-baseline asset with recent data.' })}</Text>
        {getFeedbackRaisedSpeedRangesText(activity.speed_info)}
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  health_score_model_retraining_required: {
    title: activity => i18n.t('activity.groupedActivity.healthScorePaused.title', {
      defaultValue: 'Health score paused at {{componentName}}',
      componentName: activity.component_name
    }),
    description: () => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.groupedActivity.healthScorePaused.description', { defaultValue: 'Retrain the health score models for this asset.' })}</Text>
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  // Any AHS feedback type this build has no entry for (the backend adds them over
  // time), so the feed shows a plain row instead of crashing the page.
  ahs_feedback: {
    title: activity => i18n.t('activity.groupedActivity.healthScoreFeedback.title', {
      defaultValue: 'Health score feedback at {{componentName}}',
      componentName: activity.component_name
    }),
    description: activity => (
      <DescriptionContainer>
        <Text>{humanize(activity.feedback_type)}</Text>
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  task: {
    title: activity => i18n.t('activity.groupedActivity.titles.taskBy', {
      defaultValue: '{{name}} by {{user}}',
      name: activity.name,
      user: activity.user_name
    }),
    icon: () => <Task />
  },
  repair: {
    title: activity => i18n.t('activity.groupedActivity.titles.repairBy', {
      defaultValue: '{{name}} by {{user}}',
      name: activity.name,
      user: activity.user_name
    }),
    icon: () => <Wrench />
  },
  note: {
    icon: () => <NoteSvgNew />
  }
};

export default groupedActivityFormat;
